/**
 * Shared expensive-work concurrency gate.
 *
 * A single worker-wide semaphore that bounds the TOTAL number of concurrent
 * "expensive" draws — snapshot format/augment and trace merge (cpu-offload), the
 * Jimp image ops (cpu-offload, while `browser.cpu-offload.images` is on), ffmpeg
 * encode, repository exploration, and document extraction (the one INLINE draw,
 * hence the only one whose hold is not independently timeout-bounded) — to
 * `worker.expensive.max-concurrency`.
 * This is the convergence bound: every expensive driver counts against the same
 * budget rather than each keeping a private cap, so the worker can't be
 * oversubscribed by the sum of independent pools.
 *
 * Unconfigured, the gate is unbounded (a no-op) — so the CLI, tests, and any host
 * that never calls {@link configureExpensiveGate} behave exactly as before. The
 * API host configures it from the knob on its refresh interval.
 *
 * Admission is FIFO and starvation-free: waiters are granted in arrival order and
 * every slot-hold is timeout-bounded (a draw can't hold a slot forever), so every
 * waiter advances to the head and is served. Frequent sub-second format draws
 * naturally win most slots by arrival rate; a rare background encode just occupies
 * one slot for its (bounded) duration, briefly lowering shared capacity.
 *
 * One hole in "every expensive draw counts against this budget": when a driver's
 * pool faults, its wrapper releases the slot and runs the work **inline on the
 * host**, outside the gate. A one-off is fine, but a systemic fault degrades every
 * routed call site at once and the bound stops applying. That is why those
 * fallbacks are counted — see `worker-pool/inline-fallback.ts` and the
 * `ExpensivePoolInlineFallbacks` metric.
 *
 * Not handled (deliberately): priority *inversion* — letting a latency-critical
 * UI_REALTIME draw jump ahead of an already-queued background encode. FIFO serves
 * by arrival, so that draw waits one slot-cycle. A priority-aware queue is an
 * optional future latency optimization, not required for correctness.
 *
 * @module expensive-gate
 */

/** Active cap; `Infinity` until the host configures it (= no gating). */
let maxConcurrency = Number.POSITIVE_INFINITY;
/** Slots currently held. */
let inUse = 0;
/** FIFO resolvers for callers parked waiting for a slot. */
const waiters: Array<() => void> = [];

/** Grant slots to parked waiters while capacity allows. */
function wake(): void {
  while (inUse < maxConcurrency && waiters.length > 0) {
    const next = waiters.shift();
    if (!next) break;
    inUse++;
    next();
  }
}

/**
 * Set the shared cap (typically `worker.expensive.max-concurrency`). Safe to call
 * repeatedly — e.g. on a knob refresh. A non-positive/non-finite value disables
 * the gate (unbounded). Raising the cap immediately wakes parked waiters.
 */
export function configureExpensiveGate(max: number): void {
  maxConcurrency = Number.isFinite(max) && max > 0 ? Math.floor(max) : Number.POSITIVE_INFINITY;
  wake();
}

function acquire(): Promise<void> {
  if (inUse < maxConcurrency) {
    inUse++;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => waiters.push(resolve));
}

function release(): void {
  inUse = Math.max(0, inUse - 1);
  wake();
}

/**
 * Run `fn` while holding one expensive slot — acquire (awaiting if the budget is
 * full), run, release on settle (success or throw). The single entry point so the
 * slot can never leak.
 */
export async function withExpensiveSlot<T>(fn: () => Promise<T>): Promise<T> {
  await acquire();
  try {
    return await fn();
  } finally {
    release();
  }
}

/** Current gate occupancy (for telemetry / tests). */
export function expensiveGateStats(): { maxConcurrency: number; inUse: number; queued: number } {
  return { maxConcurrency, inUse, queued: waiters.length };
}

/** @internal test seam — reset to the unconfigured (unbounded) state. */
export function _resetExpensiveGateForTest(): void {
  maxConcurrency = Number.POSITIVE_INFINITY;
  inUse = 0;
  waiters.length = 0;
}
